import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Activity, Star, Calendar, ArrowRight, MessageCircle, Bell, Briefcase, AlertCircle, Home, User, DollarSign, Clock, CheckCircle, TrendingUp, TrendingDown, FileText } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useJobStore } from '@/store/jobStore';
import { useGlobalToast } from '@/contexts/ToastContext';
import { backendJobService } from '@/services/backendJobService';
import { backendProviderService } from '@/services/backendProviderService';
import { messagingService } from '@/services/messagingService';
import { Card, CardHeader } from '@/components/ui/Card';
import { JobCard } from '@/components/ui/JobCard';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SmartLink } from '@/components/ui/SmartLink';
import { SkipLink } from '@/components/ui/SkipLink';
import { ProviderDetailsModal } from '@/components/job-detail/ProviderDetailsModal';
import { LocationInput } from '@/components/location/LocationInput';
import { SkeletonCard, SkeletonStats } from '@/components/ui/SkeletonLoaders';
import { EnhancedEmptyState } from '@/components/ui/EnhancedEmptyState';
import type { Job, ServiceProvider } from '@/types';
import {
  WrenchScrewdriverIcon,
  BoltIcon,
  PaintBrushIcon,
  SparklesIcon,
  WrenchIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CogIcon
} from '@heroicons/react/24/outline';

interface ServiceCategory {
  id?: string;
  name: string;
  icon?: any;
  color?: string;
  count: number;
  description?: string;
}

interface TrustedProvider {
  id: string;
  name: string;
  avatar: string;
  specialties: string[];
  rating: number;
  badges: string[];
}

export function CustomerDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { jobs, fetchJobs } = useJobStore();
  const { error, success } = useGlobalToast();
  const [activeJobTab, setActiveJobTab] = useState<'awaiting-quotes' | 'in-progress' | 'completed'>('awaiting-quotes');
  const [quickService, setQuickService] = useState('');
  const [quickLocation, setQuickLocation] = useState('');
  const [serviceError, setServiceError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [allServiceCategories, setAllServiceCategories] = useState<ServiceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previousQuoteCount, setPreviousQuoteCount] = useState(0);
  const [dismissedTips, setDismissedTips] = useState<string[]>([]);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null);
  const [isLoadingProviderProfile, setIsLoadingProviderProfile] = useState(false);
  const [selectedJobForProfile, setSelectedJobForProfile] = useState<Job | null>(null);
  
  // Category autocomplete state
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  
  // Location state
  const [locationCoordinates, setLocationCoordinates] = useState<{ lat: number; lng: number } | undefined>();

  useEffect(() => {
    const loadInitialData = async () => {
      console.log('🔄 CustomerDashboardPage: Loading initial data...');
      setIsLoading(true);
      await fetchJobs();
      setIsLoading(false);
      console.log('✅ CustomerDashboardPage: Initial data loaded, jobs count:', jobs.length);
    };
    
    loadInitialData();
    
    // Real-time polling for new quotes
    const interval = setInterval(() => {
      checkForNewQuotes();
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [fetchJobs]);
  
  // Re-fetch jobs when jobs array changes (e.g., after accepting a quote from another page)
  useEffect(() => {
    console.log('📊 CustomerDashboardPage: Jobs updated, current count:', jobs.length);
    if (jobs.length > 0) {
      const jobsWithQuotes = jobs.filter(j => j.quoteCount > 0);
      const jobsWithAcceptedQuotes = jobs.filter(j => !!(j.assignedProviderId || (j as any).providerId));
      console.log('📈 Jobs stats:', {
        total: jobs.length,
        withQuotes: jobsWithQuotes.length,
        withAcceptedQuotes: jobsWithAcceptedQuotes.length,
        openJobs: jobs.filter(j => normalizeStatus(j.status) === 'OPEN').length,
        inProgressJobs: jobs.filter(j => normalizeStatus(j.status) === 'IN_PROGRESS').length
      });
    }
  }, [jobs]);

  // Load dismissed tips from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('dismissedTips_customer');
    if (saved) {
      setDismissedTips(JSON.parse(saved));
    }
  }, []);

  // Close FAB menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showFabMenu && !target.closest('.fab-container')) {
        setShowFabMenu(false);
      }
    };

    if (showFabMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFabMenu]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoading(true);
        const homepageCategories = await backendJobService.getHomepageCategories();
        if (homepageCategories && homepageCategories.length > 0) {
          const mappedCategories = homepageCategories.map(cat => ({
            id: cat.id,
            name: cat.name,
            count: cat.job_count,
            icon: getHeroIcon(cat.icon_name),
            description: cat.description
          }));
          setAllServiceCategories(mappedCategories);
        }
      } catch (err) {
        setAllServiceCategories([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadCategories();
  }, []);

  // Filter categories as user types
  useEffect(() => {
    if (quickService.length >= 2) {
      const filtered = allServiceCategories.filter(cat =>
        cat.name.toLowerCase().includes(quickService.toLowerCase())
      );
      setFilteredCategories(filtered);
      setShowCategoryDropdown(filtered.length > 0);
    } else {
      setFilteredCategories([]);
      setShowCategoryDropdown(false);
    }
  }, [quickService, allServiceCategories]);

  const getHeroIcon = (iconName: string) => {
    const iconMap: { [key: string]: any } = {
      'WrenchScrewdriver': WrenchScrewdriverIcon,
      'Bolt': BoltIcon,
      'PaintBrush': PaintBrushIcon,
      'Sparkles': SparklesIcon,
      'Wrench': WrenchIcon,
      'BuildingOffice': BuildingOfficeIcon,
      'Truck': TruckIcon,
      'Cog': CogIcon
    };
    return iconMap[iconName] || CogIcon;
  };

  const checkForNewQuotes = async () => {
    try {
      await fetchJobs();
      const totalQuotes = jobs.reduce((sum, job) => sum + (job.quoteCount || 0), 0);
      
      if (previousQuoteCount > 0 && totalQuotes > previousQuoteCount) {
        success('New Quotes Received!', `You have ${totalQuotes - previousQuoteCount} new quote${totalQuotes - previousQuoteCount > 1 ? 's' : ''} to review`);
      }
      
      setPreviousQuoteCount(totalQuotes);
    } catch (err) {
      console.error('Failed to check for new quotes:', err);
    }
  };

  const handleQuickJobPost = () => {
    let hasError = false;
    if (!quickService.trim()) {
      setServiceError('Please enter what service you need');
      hasError = true;
    } else {
      setServiceError('');
    }
    if (!quickLocation.trim()) {
      setLocationError('Please enter your location');
      hasError = true;
    } else {
      setLocationError('');
    }
    if (hasError) return;
    
    // Prepare navigation state with category and location
    const navState: any = { 
      fromQuickForm: true, 
      returnTo: '/dashboard' 
    };
    
    // If user selected a category from dropdown, pass category ID
    if (selectedCategory) {
      navState.prefilledCategory = selectedCategory.id;
      navState.categoryName = selectedCategory.name;
    } else if (quickService.trim()) {
      // Try to match typed text to a category
      const matchedCategory = allServiceCategories.find(cat => 
        cat.name.toLowerCase() === quickService.toLowerCase().trim()
      );
      if (matchedCategory) {
        navState.prefilledCategory = matchedCategory.id;
        navState.categoryName = matchedCategory.name;
      } else {
        navState.service = quickService;
      }
    }
    
    // Pass location
    if (quickLocation.trim()) {
      navState.prefilledAddress = quickLocation;
    }
    
    navigate('/create-job', { state: navState });
  };

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category);
    setQuickService(category.name);
    setShowCategoryDropdown(false);
  };

  const handleLocationChange = (value: string, coordinates?: { lat: number; lng: number }) => {
    setQuickLocation(value);
    setLocationCoordinates(coordinates);
  };

  const dismissTip = (tipId: string) => {
    const newDismissed = [...dismissedTips, tipId];
    setDismissedTips(newDismissed);
    localStorage.setItem('dismissedTips_customer', JSON.stringify(newDismissed));
  };

  const getRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Normalize status to handle both 'in-progress' (backend) and 'IN_PROGRESS' (frontend) formats
  const normalizeStatus = (status: string) => {
    if (!status) return '';
    const normalized = status.toLowerCase().replace(/_/g, '-');
    if (normalized === 'in-progress') return 'IN_PROGRESS';
    return status.toUpperCase();
  };

  // Handle viewing provider profile for in-progress jobs
  const handleViewProviderProfile = async (job: Job) => {
    const providerId = job.assignedProviderId || (job as any).providerId;
    
    if (!providerId) {
      error('Error', 'Provider ID is missing');
      return;
    }

    setIsLoadingProviderProfile(true);
    setSelectedJobForProfile(job);
    
    try {
      console.log('🔄 Fetching provider profile for ID:', providerId);
      
      // Fetch provider profile from backend
      const provider = await backendProviderService.getProviderById(providerId);
      
      if (!provider) {
        error('Error', 'Provider profile not found');
        return;
      }

      console.log('✅ Provider profile fetched:', provider);
      setSelectedProvider(provider);
      setShowProviderModal(true);
    } catch (err: any) {
      console.error('❌ Failed to fetch provider profile:', err);
      error('Error', 'Failed to load provider profile. Please try again.');
    } finally {
      setIsLoadingProviderProfile(false);
    }
  };

  // Handle messaging provider from modal
  const handleMessageProvider = async (providerId: string, providerName: string, providerBusinessName: string, initialMessage?: string) => {
    if (!selectedJobForProfile || !user) {
      error('Error', 'Job information is missing');
      return;
    }

    try {
      // Determine customer and provider IDs
      const customerId = user.id;
      const customerName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Customer';
      
      // Create or get chat conversation using the job ID from selectedJobForProfile
      const chat = await messagingService.createOrGetJobChat(
        selectedJobForProfile.id,
        selectedJobForProfile.title,
        customerId,
        customerName,
        providerId,
        providerName,
        providerBusinessName
      );
      
      // If there's an initial message, send it
      if (initialMessage && initialMessage.trim()) {
        try {
          await messagingService.sendMessage(
            chat.id,
            customerId,
            customerName,
            'customer',
            initialMessage.trim()
          );
          success('Message Sent', `Your message to ${providerBusinessName} has been sent.`);
        } catch (msgErr) {
          console.error('Failed to send initial message:', msgErr);
          // Continue to navigate even if message sending fails
        }
      }
      
      // Navigate to chat page with the conversation ID
      navigate(`/chat/${chat.id}`);
      setShowProviderModal(false);
      setSelectedProvider(null);
      setSelectedJobForProfile(null);
    } catch (err: any) {
      console.error('Failed to create chat:', err);
      error('Failed to Start Conversation', 'Please try again.');
    }
  };
  
  // Jobs awaiting quotes (no accepted quote yet)
  const awaitingQuotesJobs = jobs.filter(job => {
    const status = normalizeStatus(job.status);
    const hasAcceptedQuote = !!(job.assignedProviderId || (job as any).providerId);
    // Show jobs with status 'OPEN' that don't have an accepted quote yet
    return status === 'OPEN' && !hasAcceptedQuote;
  });

  // Jobs in progress (quote accepted, work ongoing)
  const inProgressJobs = jobs.filter(job => {
    const status = normalizeStatus(job.status);
    const hasAcceptedQuote = !!(job.assignedProviderId || (job as any).providerId);
    // Show jobs with status 'IN_PROGRESS' or jobs with accepted quote (provider assigned)
    return status === 'IN_PROGRESS' || hasAcceptedQuote;
  });

  // Completed jobs
  const completedJobs = jobs.filter(job => normalizeStatus(job.status) === 'COMPLETED');

  // Displayed jobs based on active tab
  const displayedJobs = jobs.filter(j => {
    const status = normalizeStatus(j.status);
    const hasAcceptedQuote = !!(j.assignedProviderId || (j as any).providerId);
    
    if (activeJobTab === 'awaiting-quotes') {
      // Show jobs with status 'OPEN' that don't have an accepted quote yet
      return status === 'OPEN' && !hasAcceptedQuote;
    }
    if (activeJobTab === 'in-progress') {
      // Show jobs with status 'IN_PROGRESS' or jobs with accepted quote (provider assigned)
      return status === 'IN_PROGRESS' || hasAcceptedQuote;
    }
    // Completed tab
    return status === 'COMPLETED';
  });

  // Calculate stats
  const totalSpent = completedJobs.reduce((sum, job) => sum + (job.budgetMax || job.budgetMin || 0), 0);
  const jobsWithQuotes = jobs.filter(j => j.quoteCount > 0).length;
  const totalQuotes = jobs.reduce((sum, job) => sum + (job.quoteCount || 0), 0);
  const avgResponseTime = jobs.length > 0 ? '2.5h' : 'N/A'; // Placeholder

  // Action required items
  // Use normalizeStatus function defined above
  // Only count jobs with status 'OPEN' that have quotes but NO accepted quote (no provider assigned)
  const jobsWithPendingQuotes = jobs.filter(j => {
    const status = normalizeStatus(j.status);
    // Exclude jobs that have a provider assigned (quote was accepted)
    const hasAcceptedQuote = !!(j.assignedProviderId || (j as any).providerId);
    // Only show if status is 'OPEN', has quotes, and no provider assigned yet
    const shouldShow = status === 'OPEN' && j.quoteCount > 0 && !hasAcceptedQuote;
    
    // Debug logging to help identify issues
    if (j.quoteCount > 0) {
      console.log('🔍 Job with quotes:', {
        id: j.id,
        title: j.title,
        rawStatus: j.status,
        normalizedStatus: status,
        quoteCount: j.quoteCount,
        assignedProviderId: j.assignedProviderId,
        providerId: (j as any).providerId,
        hasAcceptedQuote,
        shouldShow,
        willBeCounted: shouldShow
      });
    }
    
    return shouldShow;
  });
  
  const quotesAwaitingReview = jobsWithPendingQuotes.length;
  
  // Debug: Log the final count
  console.log('📊 quotesAwaitingReview calculation:', {
    totalJobs: jobs.length,
    jobsWithQuotes: jobs.filter(j => j.quoteCount > 0).length,
    jobsWithPendingQuotes: jobsWithPendingQuotes.length,
    quotesAwaitingReview
  });
  const jobsInProgress = jobs.filter(j => {
    const status = normalizeStatus(j.status);
    return status === 'IN_PROGRESS' || status === 'IN-PROGRESS';
  }).length;
  
  // Only count new quotes for jobs that don't have accepted quotes yet
  // This should match the quotesAwaitingReview logic
  const newQuotesToday = jobs.filter(j => {
    const status = normalizeStatus(j.status);
    const hasAcceptedQuote = !!(j.assignedProviderId || (j as any).providerId);
    // Only count quotes for jobs that are still OPEN and don't have accepted quotes
    return status === 'OPEN' && j.quoteCount > 0 && !hasAcceptedQuote;
  }).length;

  const trustedProviders: TrustedProvider[] = [
    {
      id: '1', name: 'John Murphy', avatar: 'JM', specialties: ['Plumbing', 'Boiler Repair'],
      rating: 4.9, badges: ['Verified', 'Top Rated']
    },
    {
      id: '2', name: 'Sarah O\'Connor', avatar: 'SO', specialties: ['Interior Painting'],
      rating: 5.0, badges: ['Verified', 'Design Expert']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20 md:pb-8">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Dashboard' }]} />
        
        {/* Welcome Header */}
        <div className="mb-4 sm:mb-6 mt-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Welcome back, {user?.firstName}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your jobs and find trusted professionals</p>
        </div>

        {/* Action Required Section - Only show for pending quotes (not accepted) */}
        {quotesAwaitingReview > 0 && (
          <Card className="mb-4 sm:mb-6 border-l-4 border-l-primary-500">
            <div className="p-4">
              <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-primary-600" aria-hidden="true" />
                ⚡ Action Required
              </h2>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg border border-primary-200">
                  <span className="text-sm text-primary-900 font-medium">
                    🔵 {quotesAwaitingReview} quote{quotesAwaitingReview > 1 ? 's' : ''} awaiting your review
                  </span>
                  <Button 
                    size="sm" 
                    onClick={() => {
                      setActiveJobTab('awaiting-quotes');
                      // Scroll to the "My Jobs" section
                      setTimeout(() => {
                        const jobsSection = document.getElementById('my-jobs-section');
                        if (jobsSection) {
                          jobsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    aria-label={`Review ${quotesAwaitingReview} pending quote${quotesAwaitingReview > 1 ? 's' : ''}`}
                  >
                    Review
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">

            {/* Quick Post Widget */}
            <Card className="overflow-hidden">
              <CardHeader
                title="Post a New Job"
                subtitle="Get quotes from verified professionals quickly"
              />

              <div className="p-6 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Category Autocomplete */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                    <input
                      type="text"
                      placeholder="What service do you need?"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-touch ${serviceError ? 'border-red-400' : 'border-gray-300'}`}
                      value={quickService}
                      onChange={(e) => {
                        setQuickService(e.target.value);
                        setSelectedCategory(null);
                        if (e.target.value.trim()) setServiceError('');
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleQuickJobPost()}
                      onFocus={() => quickService.length >= 2 && setShowCategoryDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
                      aria-label="Search for service"
                    />
                    
                    {/* Category Autocomplete Dropdown */}
                    {showCategoryDropdown && filteredCategories.length > 0 && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {filteredCategories.map((category) => {
                          const IconComponent = category.icon;
                          return (
                            <button
                              key={category.id}
                              onClick={() => handleCategorySelect(category)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0 transition-colors"
                            >
                              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <IconComponent className="w-5 h-5 text-primary-600" />
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{category.name}</div>
                                {category.description && (
                                  <div className="text-sm text-gray-500 truncate">{category.description}</div>
                                )}
                              </div>
                              {category.count > 0 && (
                                <div className="text-xs text-gray-400">{category.count} pros</div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Selected Category Indicator */}
                    {selectedCategory && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-success-600" />
                        <span className="text-sm text-success-600 font-medium">Selected</span>
                      </div>
                    )}
                  </div>
                  {serviceError && (
                    <p className="text-red-500 text-sm mt-1 ml-1">{serviceError}</p>
                  )}

                  {/* Location Autocomplete */}
                  <div className="relative">
                    <LocationInput
                      value={quickLocation}
                      onChange={(val, coords) => { handleLocationChange(val, coords); if (val.trim()) setLocationError(''); }}
                      placeholder="Location (e.g. Dublin)"
                      className={`w-full py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-touch ${locationError ? 'border-red-400' : 'border-gray-300'}`}
                      showSavedAddresses={true}
                      showCurrentLocation={true}
                    />
                  </div>
                  {locationError && (
                    <p className="text-red-500 text-sm mt-1 ml-1">{locationError}</p>
                  )}
                </div>

                <Button
                  fullWidth
                  size="lg"
                  onClick={handleQuickJobPost}
                  variant="primary"
                >
                  Get Quotes Now
                </Button>

                <div className="mt-8">
                  <p className="text-sm font-semibold text-gray-900 mb-3">Popular Services</p>
                  <div className="flex flex-wrap gap-2">
                    {allServiceCategories.slice(0, 5).map((category) => (
                      <button
                        key={category.name}
                        onClick={() => {
                          setSelectedCategory(category);
                          setQuickService(category.name);
                        }}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm transition-colors"
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* My Jobs Section */}
            <div id="my-jobs-section">
              <Card>
                <div className="border-b border-gray-200 p-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-bold text-gray-900">My Jobs</h2>
                </div>

                <div className="flex bg-gray-100 p-1 rounded-lg">
                  {[
                    { id: 'awaiting-quotes', label: 'Awaiting Quotes', count: awaitingQuotesJobs.length },
                    { id: 'in-progress', label: 'In Progress', count: inProgressJobs.length },
                    { id: 'completed', label: 'Completed', count: completedJobs.length }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveJobTab(tab.id as any)}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${activeJobTab === tab.id
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      <span>{tab.label}</span>
                      {tab.count > 0 && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          activeJobTab === tab.id
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6 space-y-4">
                {isLoading ? (
                  <div className="space-y-3">
                    <SkeletonCard />
                    <SkeletonCard />
                    <SkeletonCard />
                  </div>
                ) : displayedJobs.length > 0 ? (
                  displayedJobs.map(job => {
                    const hasAssignedProvider = !!(job.assignedProviderId || (job as any).providerId);
                    const isInProgressTab = activeJobTab === 'in-progress';
                    
                    return (
                      <div key={job.id} className="relative">
                        <JobCard
                          job={job}
                          compact
                          onView={(id) => navigate(`/jobs/${id}`)}
                          showQuoteCount
                        />
                        {/* View Profile button for in-progress jobs with assigned provider */}
                        {isInProgressTab && hasAssignedProvider && (
                          <div className="mt-2 px-4">
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              onClick={() => handleViewProviderProfile(job)}
                              disabled={isLoadingProviderProfile && selectedJobForProfile?.id === job.id}
                              icon={<User className="w-4 h-4" />}
                            >
                              {isLoadingProviderProfile && selectedJobForProfile?.id === job.id ? 'Loading...' : 'View Provider Profile'}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <EnhancedEmptyState
                    icon={Briefcase}
                    title="No jobs found in this category"
                    description={
                      activeJobTab === 'awaiting-quotes' 
                        ? 'Jobs waiting for quotes or with pending quotes will appear here'
                        : activeJobTab === 'in-progress'
                        ? 'Jobs with accepted quotes and work in progress will appear here'
                        : 'Completed jobs will appear here'
                    }
                    action={{
                      label: 'Post a New Job',
                      onClick: () => navigate('/create-job'),
                      icon: <Briefcase className="w-4 h-4" />
                    }}
                    size="sm"
                    illustration="pattern"
                  />
                )}
              </div>
            </Card>
            </div>
          </div>

          <div className="space-y-6">
            {/* Stats Card */}
            <Card>
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary-600" />
                  Your Activity
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {/* Total Spent */}
                <div className="p-3 bg-primary-50 rounded-lg border-l-4 border-l-primary-500">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-medium text-gray-600">Total Spent</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">€{totalSpent.toLocaleString()}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {completedJobs.length} completed job{completedJobs.length !== 1 ? 's' : ''}
                  </div>
                </div>

                {/* In Progress Jobs */}
                <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-l-blue-500">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-gray-600">In Progress</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">{inProgressJobs.length}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {awaitingQuotesJobs.length} awaiting quotes
                  </div>
                </div>

                {/* Quotes Received */}
                <div className="p-3 bg-warning-50 rounded-lg border-l-4 border-l-warning-500">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-warning-600" />
                    <span className="text-xs font-medium text-gray-600">Quotes Received</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">{totalQuotes}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Avg response: {avgResponseTime}
                  </div>
                </div>

                {/* Completion Rate */}
                <div className="p-3 bg-emerald-50 rounded-lg border-l-4 border-l-emerald-500">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-medium text-gray-600">Satisfaction</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">
                    {completedJobs.length > 0 ? '100%' : 'N/A'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Based on completed jobs
                  </div>
                </div>
              </div>
            </Card>

            {/* Recommended Professionals */}
            <Card>
              <CardHeader title="Recommended Pros" icon={<Star className="w-5 h-5 text-warning-500" />} />
              <div className="p-4 space-y-4">
                {trustedProviders.map((provider) => (
                  <div key={provider.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center font-bold text-gray-600 text-sm">
                        {provider.avatar}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{provider.name}</h4>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-warning-500 fill-warning-500" />
                          <span className="text-xs font-semibold">{provider.rating}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate('/create-job', { state: { preferredProvider: provider.id } })}
                    >
                      Hire
                    </Button>
                  </div>
                ))}
              </div>
              <div className="p-4 pt-0">
                <Button fullWidth variant="ghost" onClick={() => navigate('/providers')}>
                  View All Providers
                </Button>
              </div>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardHeader title="Quick Actions" />
              <div className="p-4 space-y-2">
                <Button
                  fullWidth
                  variant="outline"
                  onClick={() => navigate('/create-job')}
                  icon={<Briefcase />}
                >
                  Post a New Job
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  onClick={() => navigate('/providers')}
                  icon={<Search />}
                >
                  Find Professionals
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  onClick={() => navigate('/customer-jobs')}
                  icon={<Calendar />}
                >
                  View All Jobs
                </Button>
              </div>
            </Card>

            {/* Tips & Insights */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="p-4">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  💡 Tips & Insights
                </h3>
                <div className="space-y-2 text-sm text-blue-800">
                  {quotesAwaitingReview > 0 && !dismissedTips.includes('review-quotes') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('review-quotes')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Review Your Quotes</p>
                      <p className="text-xs pr-4">You have {quotesAwaitingReview} quote{quotesAwaitingReview > 1 ? 's' : ''} waiting. Compare and choose the best provider!</p>
                    </div>
                  )}
                  {(awaitingQuotesJobs.length === 0 && inProgressJobs.length === 0) && !dismissedTips.includes('post-first-job') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('post-first-job')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Get Started</p>
                      <p className="text-xs pr-4">Post your first job and receive quotes from verified professionals within hours!</p>
                    </div>
                  )}
                  {completedJobs.length > 0 && completedJobs.length < 3 && !dismissedTips.includes('leave-review') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('leave-review')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Leave a Review</p>
                      <p className="text-xs pr-4">Help others by reviewing your completed jobs. Your feedback matters!</p>
                    </div>
                  )}
                  {jobsWithQuotes === 0 && awaitingQuotesJobs.length > 0 && !dismissedTips.includes('wait-for-quotes') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('wait-for-quotes')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Quotes Coming Soon</p>
                      <p className="text-xs pr-4">Professionals typically respond within 2-4 hours. You'll be notified!</p>
                    </div>
                  )}
                  {totalQuotes > 3 && !dismissedTips.includes('compare-quotes') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('compare-quotes')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Compare Carefully</p>
                      <p className="text-xs pr-4">Don't just pick the lowest price. Check ratings, reviews, and response time!</p>
                    </div>
                  )}
                  {completedJobs.length >= 3 && !dismissedTips.includes('trusted-provider') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('trusted-provider')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Build Relationships</p>
                      <p className="text-xs pr-4">Found a great provider? Save them to your favorites for future jobs!</p>
                    </div>
                  )}
                  {/* Show default tip if all are dismissed */}
                  {dismissedTips.length >= 6 && (
                    <div className="bg-white/60 p-3 rounded-lg text-center">
                      <p className="text-xs">You're all set! Keep posting jobs to find great professionals 🎉</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Floating Action Button for Mobile */}
      <div className="fixed bottom-20 right-4 md:hidden z-30">
        <div className="relative group fab-container">
          <button
            className="w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 transition-all"
            onClick={() => setShowFabMenu(!showFabMenu)}
          >
            <span className="text-2xl font-bold">+</span>
          </button>
          
          {showFabMenu && (
            <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 w-48">
              <button
                onClick={() => {
                  navigate('/create-job');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <Briefcase className="w-4 h-4 text-primary-600" />
                <span>Post a Job</span>
              </button>
              <button
                onClick={() => {
                  navigate('/providers');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <Search className="w-4 h-4 text-blue-600" />
                <span>Find Professionals</span>
              </button>
              <button
                onClick={() => {
                  navigate('/customer-jobs');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <Calendar className="w-4 h-4 text-purple-600" />
                <span>My Jobs</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 md:hidden z-40 safe-area-inset-bottom">
        <div className="grid grid-cols-4 gap-1 p-2">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg bg-primary-50 text-primary-600"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs font-medium">Home</span>
          </button>
          <button
            onClick={() => navigate('/customer-jobs')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600 relative"
          >
            <Briefcase className="w-5 h-5" />
            <span className="text-xs">Jobs</span>
            {(awaitingQuotesJobs.length > 0 || inProgressJobs.length > 0) && (
              <span className="absolute top-1 right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {(awaitingQuotesJobs.length + inProgressJobs.length) > 9 ? '9+' : (awaitingQuotesJobs.length + inProgressJobs.length)}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/providers')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600 relative"
          >
            <Search className="w-5 h-5" />
            <span className="text-xs">Find</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <User className="w-5 h-5" />
            <span className="text-xs">Profile</span>
          </button>
        </div>
      </div>

      {/* Provider Profile Modal */}
      {showProviderModal && selectedProvider && (
        <ProviderDetailsModal
          provider={selectedProvider}
          onClose={() => {
            setShowProviderModal(false);
            setSelectedProvider(null);
            setSelectedJobForProfile(null);
          }}
          onMessage={handleMessageProvider}
        />
      )}
    </div>
  );
}
